import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Lightning, Plus, Play, ChartBar, Copy, Download, Upload, Trash, X, PencilSimple, Bell } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import WorkflowBuilder from "../components/workflow/WorkflowBuilder";
import TemplatesModal from "../components/workflow/TemplatesModal";
import SimulateModal from "../components/workflow/SimulateModal";
import StatsPanel from "../components/workflow/StatsPanel";
import { triggerLabel } from "../components/workflow/blocks";

export default function Automazioni() {
  const [list, setList] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sysRules, setSysRules] = useState(null);
  const [tab, setTab] = useState("automazioni");
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [simulateId, setSimulateId] = useState(null);
  const [statsFor, setStatsFor] = useState(null);
  const fileInput = useRef(null);

  const load = useCallback(() => {
    api.get("/automations").then((r) => setList(r.data)).catch(() => {});
    api.get("/automations/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/automations/system-rules").then((r) => setSysRules(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // deep-link dal pulsante play della riga coupon: /automazioni?edit={id} apre il builder
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && list.length) {
      const a = list.find((x) => x.id === editId);
      if (a) { setEditing(a); setView("builder"); }
      setSearchParams({}, { replace: true });
    }
  }, [list, searchParams, setSearchParams]);

  const toggle = async (id) => {
    try { await api.post(`/automations/${id}/toggle`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/automations/${id}/duplicate`);
      toast.success("Workflow duplicato (disattivato)");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/automations/${id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const exportOne = async (a) => {
    try {
      const r = await api.get(`/automations/${a.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `workflow-${a.name.replace(/\s+/g, "-").toLowerCase()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      await api.post("/automations/import", { data });
      toast.success("Workflow importato (disattivato)");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "File non valido");
    } finally {
      e.target.value = "";
    }
  };

  const runDue = async () => {
    try {
      const r = await api.post("/automations/run-due");
      toast.success(`${r.data.executed} azioni eseguite`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (view === "builder") {
    return (
      <WorkflowBuilder
        automation={editing}
        onBack={() => { setView("list"); setEditing(null); load(); }}
        onSaved={load}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="automazioni-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Automazioni</h1>
          <p className="text-xs text-zinc-500">Workflow visuale: evento → attese → condizioni → azioni</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={runDue} data-testid="btn-run-due"
            className="h-10 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
            <Play size={14} weight="bold" /> Esegui programmate
          </button>
          <button onClick={() => fileInput.current?.click()} data-testid="btn-import"
            className="h-10 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
            <Upload size={14} weight="bold" /> Importa
          </button>
          <input ref={fileInput} type="file" accept=".json" onChange={importFile} className="hidden" data-testid="import-file" />
          <button onClick={() => setShowTemplates(true)} data-testid="btn-templates"
            className="h-10 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
            <Lightning size={14} weight="bold" /> Template
          </button>
          <button onClick={() => { setEditing(null); setView("builder"); }} data-testid="btn-nuova-automazione"
            className="h-10 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2">
            <Plus size={16} weight="bold" /> Nuova
          </button>
        </div>
      </div>

      {tab === "automazioni" && sysRules && (
        <section className="bg-white border border-cyan-200 rounded-lg p-4" data-testid="system-rules">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <h2 className="font-display font-semibold text-zinc-900">Automazioni di sistema</h2>
              <p className="text-xs text-zinc-500">Comunicazioni automatiche delle prenotazioni, sempre attive senza workflow. I testi si personalizzano in Impostazioni → Testi e notifiche.</p>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">SEMPRE ATTIVE</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {sysRules.rules.map((r) => (
              <div key={r.key} className="flex items-center gap-2.5 border border-zinc-100 rounded-md px-3 py-2" data-testid={`sysrule-${r.key}`}>
                <Bell size={15} weight="bold" className="text-cyan-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-zinc-900">{r.label}</div>
                  <div className="text-[11px] text-zinc-600">
                    {r.channels.map((c) => c === "whatsapp"
                      ? (sysRules.channels.whatsapp ? "WhatsApp ✓" : "WhatsApp (non attivo)")
                      : "Email ✓").join(" · ")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="flex gap-1.5">
        {[["automazioni", "Workflow"], ["log", `Storico (${logs.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`h-9 px-4 rounded-full text-xs font-semibold border ${tab === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "automazioni" && (
        <div className="space-y-2" data-testid="automations-list">
          {list.length === 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg py-10 text-center">
              <p className="text-sm text-zinc-400">Nessuna automazione. Creane una nuova o parti da un template.</p>
            </div>
          )}
          {list.map((a) => (
            <div key={a.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`automation-${a.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <Lightning size={18} weight="fill" className={a.active ? "text-amber-500" : "text-zinc-300"} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-zinc-900 flex items-center gap-2">
                    {a.name}
                    {a.nodes?.length > 0 && <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5">WORKFLOW</span>}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {triggerLabel(a.trigger)}{a.nodes?.length ? ` · ${a.nodes.length} blocchi` : ""} · {a.executions} esecuzioni{a.scheduled > 0 ? ` · ${a.scheduled} in coda` : ""}
                  </div>
                </div>
                <button onClick={() => toggle(a.id)} data-testid={`toggle-${a.id}`}
                  className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${a.active ? "bg-emerald-500" : "bg-zinc-300"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${a.active ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </div>
              <div className="flex gap-1.5 mt-3 flex-wrap">
                <button onClick={() => { setEditing(a); setView("builder"); }} data-testid={`edit-${a.id}`}
                  className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
                  <PencilSimple size={13} weight="bold" /> Apri builder
                </button>
                {a.nodes?.length > 0 && (
                  <button onClick={() => setSimulateId(a.id)} data-testid={`simulate-${a.id}`}
                    className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                    <Play size={13} weight="bold" /> Simula
                  </button>
                )}
                <button onClick={() => setStatsFor(a)} data-testid={`stats-${a.id}`}
                  className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                  <ChartBar size={13} weight="bold" /> Statistiche
                </button>
                <button onClick={() => duplicate(a.id)} data-testid={`duplicate-${a.id}`}
                  className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                  <Copy size={13} /> Duplica
                </button>
                <button onClick={() => exportOne(a)} data-testid={`export-${a.id}`}
                  className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                  <Download size={13} /> Esporta
                </button>
                <button onClick={() => remove(a.id)} data-testid={`delete-${a.id}`}
                  className="h-9 px-3 rounded-md bg-white border border-rose-200 text-xs font-semibold text-rose-600 flex items-center gap-1.5">
                  <Trash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "log" && (
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="logs-list">
          {logs.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessuna esecuzione</div>}
          {logs.map((l) => (
            <div key={l.id} className="px-4 py-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-zinc-900">{l.automation_name}</span>
                <span className="text-xs text-zinc-400">{new Date(l.executed_at).toLocaleString("it-IT")}</span>
              </div>
              <div className="text-xs text-zinc-500">
                {l.action?.replaceAll("_", " ")} ({l.channel}) {l.customer_name ? `· ${l.customer_name}` : ""}
              </div>
              {l.message && <div className="text-xs text-zinc-600 mt-1 italic">"{l.message}"</div>}
              {l.survey_token && (
                <a href={`/s/${l.survey_token}`} target="_blank" rel="noreferrer" className="text-xs text-zinc-900 underline">
                  Link sondaggio generato
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {showTemplates && (
        <TemplatesModal onClose={() => setShowTemplates(false)} onCreated={(a) => { setShowTemplates(false); setEditing(a); setView("builder"); }} />
      )}
      {simulateId && <SimulateModal automationId={simulateId} onClose={() => setSimulateId(null)} />}
      {statsFor && <StatsPanel automationId={statsFor.id} automationName={statsFor.name} onClose={() => setStatsFor(null)} />}
    </div>
  );
}
